import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { useCookies } from 'react-cookie';
import { useGetUserID } from "../hooks/useGetUserID";
import VideoPlayer from '../components/VideoPlayer';
import '../components/VideoPlayer.css';
import axios from 'axios';
import { API_BASE_URL } from '../config';

const FRANKIE_RECIPE_ID = '699061e7209dd85e7cbea5a7';
const FRANKIE_PROMPT_SHOWN_KEY = 'frankie_prompt_shown';

export default function Home() {
    const navigate = useNavigate();
    const [cookies] = useCookies(["access_token"]);
    const userID = useGetUserID();
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [showContactModal, setShowContactModal] = useState(false);
    const [showFrankiePrompt, setShowFrankiePrompt] = useState(false);
    const [contactForm, setContactForm] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [contactFormStatus, setContactFormStatus] = useState({ type: '', message: '' });

    const handleStartJourney = () => {
        if (!cookies.access_token || !userID) {
            sessionStorage.setItem('redirectAfterLogin', '/createrecipe');
            setShowAuthModal(true);
        } else {
            navigate('/createrecipe');
        }
    };

    const handleAuthSuccess = () => {
        setShowAuthModal(false);
        navigate('/createrecipe');
    };

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        setContactFormStatus({ type: '', message: '' });

        try {
            const response = await axios.post(`${API_BASE_URL}/contact/submit`, contactForm);
            
            if (response.data.success) {
                setContactFormStatus({ type: 'success', message: response.data.message });
                setContactForm({ name: '', email: '', subject: '', message: '' });
                setTimeout(() => {
                    setShowContactModal(false);
                    setContactFormStatus({ type: '', message: '' });
                }, 2000);
            }
        } catch (error) {
            setContactFormStatus({ 
                type: 'error', 
                message: error.response?.data?.message || 'Failed to send message. Please try again.' 
            });
        }
    };

    const handleContactInputChange = (e) => {
        setContactForm({
            ...contactForm,
            [e.target.name]: e.target.value
        });
    };

    // Show "Want to give Frankie a go?" popup once per session
    useEffect(() => {
        const alreadyShown = sessionStorage.getItem(FRANKIE_PROMPT_SHOWN_KEY);
        if (!alreadyShown) {
            const timer = setTimeout(() => {
                setShowFrankiePrompt(true);
                sessionStorage.setItem(FRANKIE_PROMPT_SHOWN_KEY, 'true');
            }, 1500);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleFrankieYes = () => {
        setShowFrankiePrompt(false);
        navigate(`/recipe/${FRANKIE_RECIPE_ID}`, { state: { openFrankie: true } });
    };

    const handleFrankieNo = () => {
        setShowFrankiePrompt(false);
    };

    // Image data with search links
    const recipeImages = [
        {
            src: "/displayimages/pjkj8khsitkpqohiw9hd.png",
            alt: "Delicious Chicken Dishes",
            title: "Chicken Protein Meals",
            description: "Get That Gains In",
            searchQuery: "chicken",
            category: "flavoured chicken"
        },
        {
            src: "/displayimages/curry.png",
            alt: "South African Curries",
            title: "Cape Malay Curries",
            description: "Bring The Heat",
            searchQuery: "curry",
            category: "cape malay indian"
        },
        {
            src: "/displayimages/Braai.png",
            alt: "Braai & Sides",
            title: "Braai & Sides",
            description: "Impress the Boys",
            searchQuery: "braai",
            category: "braai & side dishes"
        },
        {
            src: "/displayimages/Ama.png",
            alt: "New recipes",
            title: "Traditional South African",
            description: "Amagwinya! Vetkoek!",
            searchQuery: null,
            category: null
        }
    ];

    // Function to render image card with search link
    const renderImageCard = (imageData, index) => {
        const handleClick = () => {
            const params = [];
            
            if (imageData.searchQuery) {
                params.push(`query=${encodeURIComponent(imageData.searchQuery)}`);
            }
            
            if (imageData.category) {
                // Encode category: first encode all special chars, then replace %20 (spaces) with +
                const categoryEncoded = encodeURIComponent(imageData.category).replace(/%20/g, '+');
                params.push(`category=${categoryEncoded}`);
            }
            
            const queryString = params.join('&');
            const url = queryString ? `/searchrecipes?${queryString}` : '/searchrecipes';
            navigate(url);
        };

        return (
            <div 
                key={index}
                className="card cursor-pointer hover:shadow-lg transition-shadow duration-200"
                onClick={handleClick}
            >
                <div className="w-full h-48 sm:h-40 lg:h-32 rounded-xl mb-3 overflow-hidden">
                    <img 
                        src={imageData.src} 
                        alt={imageData.alt} 
                        className="w-full h-full object-cover"
                    />
                </div>
                <h3 className="font-heading text-lg sm:text-xl text-gray-900">{imageData.title}</h3>
                <p className="text-sm sm:text-base text-gray-600">{imageData.description}</p>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-white" style={{marginTop: '-96px', paddingTop: '96px'}}>
            {/* Modern Hero Section - Mob-inspired */}
            <div className="hero" style={{
                paddingTop: '0.5rem', 
                paddingBottom: '2rem',
                minHeight: 'auto',
                alignItems: 'flex-start'
            }}>
                <div className="container">
                    <div className="hero-content">
                        <div className="grid lg:grid-cols-2 gap-8 sm:gap-16 items-center">
                            {/* Left Content - Text */}
                            <div className="space-y-4 sm:space-y-6">

                                
                                <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-gray-900 leading-tight">
                                <br />
                                    <span style={{color: '#374151', fontWeight: 'bold'}}>Eat Well.</span>
                                    <br />
                                    <span style={{color: '#374151', fontWeight: 'bold'}}>Spend Less.</span>
                                    <br />
                                    <span style={{color: '#374151', fontWeight: 'bold'}}>Stress Less.</span>
                                    <br />
                                    
                                </h1>                        
                                
                                <div className="space-y-6">
                                    {/* <p className="text-xl text-gray-700 font-body leading-relaxed max-w-lg">
                                        Transform your kitchen into a cultural journey. Create, cost, and cook 
                                        traditional recipes with modern precision.
                                    </p> */}
                                    
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                                                <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <span className="text-gray-700 font-medium">Unlimited access to South African Recipes</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                                                <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <span className="text-gray-700 font-medium">Cost and Plan Meals Easy</span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center">
                                                <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                            </div>
                                            <span className="text-gray-700 font-medium">Start Cooking with Confidence</span>
                                        </div>
                                    </div>
                                    
                                    <div className="flex flex-col sm:flex-row gap-3">
                                        {/* <button 
                                            onClick={handleStartJourney}
                                            className="btn-primary"
                                        >
                                            Start Your Free Trial
                                        </button>
                                         */}
                                        <button 
                                            onClick={() => navigate('/searchrecipes')}
                                            className="btn-secondary"
                                        >
                                            Explore Recipes
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Right Content - Food Images Grid */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                                <div className="space-y-3 sm:space-y-4">
                                    {renderImageCard(recipeImages[0], 0)}
                                    {renderImageCard(recipeImages[1], 1)}
                                </div>
                                <div className="space-y-3 sm:space-y-4 sm:mt-6">
                                    {renderImageCard(recipeImages[2], 2)}
                                    {renderImageCard(recipeImages[3], 3)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modern Features Section */}
            <div className="section bg-white">
                <div className="container">
                    <div className="text-center mb-16">
                        <h2 className="font-heading text-4xl sm:text-5xl text-gray-900 mb-6">
                            What are YOU Waiting for South Africa!!
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Joing me in this journey to make cooking easier and more affordable.
                        </p>
                    </div>

                    {/* Feature Cards Grid */}
                    <div className="grid-4 mb-16">
                        <div className="feature-card">
                            <div className="feature-icon orange">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                            </div>
                            <h3 className="font-heading text-xl text-gray-900 mb-4">AI Meal Planning & Photo Detection</h3>
                            <p className="text-gray-600 font-body">
                                Plan your week with AI meal planning, or add a photo of your fridge or pantry and our AI 
                                will name the ingredients from the photo so you can search recipes in seconds.
                            </p>
                        </div>

                        <div className="feature-card">
                            <div className="feature-icon green">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                </svg>
                            </div>
                            <h3 className="font-heading text-xl text-gray-900 mb-4">Smart Cost Tracking</h3>
                            <p className="text-gray-600 font-body">
                                Know exactly what your meals cost before you shop. Our pricing engine helps you 
                                cook delicious food that fits your budget perfectly.
                            </p>
                        </div>

                        <div className="feature-card">
                            <div className="feature-icon purple">
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            <h3 className="font-heading text-xl text-gray-900 mb-4">Smart Searching</h3>
                            <p className="text-gray-600 font-body">
                                Search for recipes by cupboard ingredients, cuisine, or dietary preferences.
                            </p>
                        </div>

                        <div className="feature-card">
                            <div className="feature-icon" style={{backgroundColor: '#fef3c7', color: '#f59e0b'}}>
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h3 className="font-heading text-xl text-gray-900 mb-4">Cost-Effective Meal Planning</h3>
                            <p className="text-gray-600 font-body">
                                Plan your weekly meals with built-in cost tracking. Get shopping lists and budget breakdowns 
                                to maximize savings while eating well.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Interactive Demo Section */}


            {/* Modern CTA Section */}
            

            {/* Footer Section */}
            <footer className="bg-gray-900 text-white py-12">
                <div className="container">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
                        {/* Brand Section */}
                        <div>
                            <h3 className="font-heading text-2xl mb-4 !text-white">WHAT TO MAKE</h3>
                            <p className="!text-white font-body">
                                Discover authentic South African recipes and cook with confidence.
                            </p>
                        </div>

                        {/* Social Media Section */}
                        <div>
                            <h4 className="font-heading text-lg mb-4 !text-white">Follow Us</h4>
                            <div className="flex flex-col space-y-3">
                                <a 
                                    href="https://www.instagram.com/what_to_make_sa/" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center space-x-3 !text-white hover:!text-orange-400 transition-colors"
                                >
                                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                    </svg>
                                    <span className="font-body !text-white">Instagram</span>
                                </a>
                                <a 
                                    href="https://www.youtube.com/@whattomakesa" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center space-x-3 !text-white hover:!text-orange-400 transition-colors"
                                >
                                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                    </svg>
                                    <span className="font-body !text-white">YouTube</span>
                                </a>
                                <a 
                                    href="https://x.com/What_To_Make_SA" 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="flex items-center space-x-3 !text-white hover:!text-orange-400 transition-colors"
                                >
                                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                                    </svg>
                                    <span className="font-body !text-white">X (Twitter)</span>
                                </a>
                            </div>
                        </div>

                        {/* Contact Section */}
                        <div>
                            <h4 className="font-heading text-lg mb-4 !text-white">Get in Touch</h4>
                            <button
                                onClick={() => setShowContactModal(true)}
                                className="bg-orange-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-orange-700 transition-colors duration-200 font-body"
                            >
                                Contact Us
                            </button>
                        </div>
                    </div>

                    <div className="border-t border-gray-800 pt-8 text-center">
                        <p className="!text-white font-body text-sm">
                            © {new Date().getFullYear()} What To Make. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>

            {/* Auth Modal */}
            {showAuthModal && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-6 sm:p-8 max-w-sm w-full shadow-xl">
                        <h2 className="font-heading text-xl sm:text-2xl text-gray-800 mb-3 sm:mb-4 text-center">Join What To Make</h2>
                        <p className="font-body text-sm sm:text-base text-gray-600 mb-6 sm:mb-8 text-center">
                            Sign up to start creating and saving recipes with cost tracking.
                        </p>
                        <div className="space-y-3 sm:space-y-4">
                            <button
                                className="w-full px-6 py-3 sm:py-4 bg-orange-600 text-white font-medium hover:bg-orange-700 transition-colors duration-200 font-body text-sm sm:text-base"
                                onClick={() => {
                                    setShowAuthModal(false);
                                    navigate('/auth/login');
                                }}
                            >
                                Get Started
                            </button>
                            <button
                                className="w-full px-6 py-3 sm:py-4 bg-gray-200 text-gray-700 font-medium hover:bg-gray-300 transition-colors duration-200 font-body text-sm sm:text-base"
                                onClick={() => setShowAuthModal(false)}
                            >
                                Maybe Later
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Contact Modal */}
            {showContactModal && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg p-6 sm:p-8 max-w-lg w-full shadow-xl max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="font-heading text-xl sm:text-2xl text-gray-800">Contact Us</h2>
                            <button
                                onClick={() => {
                                    setShowContactModal(false);
                                    setContactFormStatus({ type: '', message: '' });
                                }}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <form onSubmit={handleContactSubmit} className="space-y-4">
                            <div>
                                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1 font-body">
                                    Name *
                                </label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={contactForm.name}
                                    onChange={handleContactInputChange}
                                    required
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 font-body"
                                />
                            </div>

                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1 font-body">
                                    Email *
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={contactForm.email}
                                    onChange={handleContactInputChange}
                                    required
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 font-body"
                                />
                            </div>

                            <div>
                                <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1 font-body">
                                    Subject
                                </label>
                                <input
                                    type="text"
                                    id="subject"
                                    name="subject"
                                    value={contactForm.subject}
                                    onChange={handleContactInputChange}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 font-body"
                                />
                            </div>

                            <div>
                                <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1 font-body">
                                    Message *
                                </label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={contactForm.message}
                                    onChange={handleContactInputChange}
                                    required
                                    rows={5}
                                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 font-body resize-none"
                                />
                            </div>

                            {contactFormStatus.message && (
                                <div className={`p-3 rounded-lg ${
                                    contactFormStatus.type === 'success' 
                                        ? 'bg-green-100 text-green-700' 
                                        : 'bg-red-100 text-red-700'
                                }`}>
                                    <p className="font-body text-sm">{contactFormStatus.message}</p>
                                </div>
                            )}

                            <div className="flex gap-3">
                                <button
                                    type="submit"
                                    className="flex-1 px-6 py-3 bg-orange-600 text-white font-medium hover:bg-orange-700 transition-colors duration-200 rounded-lg font-body"
                                >
                                    Send Message
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowContactModal(false);
                                        setContactFormStatus({ type: '', message: '' });
                                    }}
                                    className="px-6 py-3 bg-gray-200 text-gray-700 font-medium hover:bg-gray-300 transition-colors duration-200 rounded-lg font-body"
                                >
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Frankie "Give it a go" prompt */}
            {showFrankiePrompt && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 sm:p-6" style={{ paddingLeft: 'max(1rem, env(safe-area-inset-left))', paddingRight: 'max(1rem, env(safe-area-inset-right))', paddingBottom: 'max(1rem, env(safe-area-inset-bottom))', paddingTop: 'max(1rem, env(safe-area-inset-top))' }}>
                    <div className="bg-white rounded-2xl p-6 sm:p-8 max-w-sm w-full shadow-xl max-h-[90vh] overflow-y-auto">
                        <h2 className="font-heading text-xl sm:text-2xl text-gray-800 mb-3 sm:mb-4 text-center">Want to give Frankie a go?</h2>
                        <p className="font-body text-sm sm:text-base text-gray-600 mb-6 sm:mb-8 text-center">
                        Got a question about a recipe? Frankie's got you covered. Choose between Afrikaans and English and Go.                       </p>
                        <div className="space-y-3 sm:space-y-4">
                            <button
                                type="button"
                                className="w-full min-h-[48px] px-6 py-3.5 sm:py-4 bg-orange-600 text-white font-medium hover:bg-orange-700 active:bg-orange-800 transition-colors duration-200 font-body text-sm sm:text-base rounded-xl touch-manipulation"
                                style={{ color: '#ffffff' }}
                                onClick={handleFrankieYes}
                            >
                                Yes, let&apos;s go
                            </button>
                            <button
                                type="button"
                                className="w-full min-h-[48px] px-6 py-3.5 sm:py-4 bg-gray-200 text-gray-700 font-medium hover:bg-gray-300 active:bg-gray-400 transition-colors duration-200 font-body text-sm sm:text-base rounded-xl touch-manipulation"
                                onClick={handleFrankieNo}
                            >
                                No thanks
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}